/**
 * installRefrigerant — REFRIG-01 on the installer's phone (Seth 2026-09-25).
 *
 * Two places the crew already works, nothing new to find:
 *   · The unit going in (Equipment step) — a refrigerant row under each outdoor /
 *     packaged unit: the refrigerant and factory charge read off the data tag,
 *     then Log the Charge: line set length (Pegasus does the "how much to add"
 *     math), what they weighed in, the total, and the readings the paper Install
 *     Order asked for (who soldered the line set, leak test psi, microns).
 *   · The old unit coming out — Refrigerant Recovered: type (from the old tag),
 *     pounds + ounces, which recovery tank (or "it was empty"). That row is the
 *     EPA 608 disposal record.
 * Only equipment marked "Uses refrigerant" in Settings asks — a furnace-only job
 * never sees any of it. Data: features/jobs/refrigerant.ts.
 */
import { useMemo, useState } from "react";
import { AlertTriangle, Calculator, Check, Loader2, Minus, Plus, Snowflake, Tag } from "lucide-react";
import { cn } from "@/lib/cn";
import { haptic } from "@/native/boot";
import { InfoTip } from "@/components/InfoTip";
import type { Job, JobEquipment } from "@/features/jobs/jobs";
import type { RemovedUnit } from "@/features/jobs/installField";
import {
  REFRIGERANTS, ozText, splitOz, suggestedAddOz, useSaveCharge, useSaveRecovery, useRefrigerantTanks, useAddTank,
  type RefrigerantLog, type RefrigerantTank,
} from "@/features/jobs/refrigerant";
import { Sheet, card, input } from "./installWork";

const numOrNull = (s: string) => {
  const t = s.replace(/[^0-9.\-]/g, "");
  if (t === "" || t === "-" || t === ".") return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
};

/** Pounds + ounces, the way a refrigerant scale reads. Value is total ounces. */
export function LbOz({ oz, onChange, label }: { oz: number; onChange: (oz: number) => void; label?: string }) {
  const { lb, oz: rest } = splitOz(oz);
  const set = (l: number, o: number) => onChange(Math.max(0, Math.round((l * 16 + o) * 10) / 10));
  const box = "h-11 w-14 rounded-xl border border-slate-200 bg-white text-center text-[17px] font-bold tabular-nums text-slate-900 focus:border-slate-400 focus:outline-none";
  const btn = "flex h-11 w-9 items-center justify-center rounded-xl bg-slate-100 text-slate-700 [touch-action:manipulation] active:bg-slate-200";
  return (
    <div className="flex items-center gap-1.5" aria-label={label}>
      <button type="button" className={btn} aria-label="One pound less" onClick={() => set(Math.max(0, lb - 1), rest)}><Minus className="h-4 w-4" /></button>
      <input inputMode="numeric" value={String(lb)} aria-label="Pounds"
        onChange={(e) => set(Number(e.target.value.replace(/\D/g, "") || 0), rest)} className={box} />
      <span className="text-[12px] font-bold text-slate-500">lb</span>
      <input inputMode="decimal" value={String(rest)} aria-label="Ounces"
        onChange={(e) => { const n = Number(e.target.value.replace(/[^0-9.]/g, "") || 0); set(lb, Math.min(n, 15.9)); }} className={box} />
      <span className="text-[12px] font-bold text-slate-500">oz</span>
      <button type="button" className={btn} aria-label="One ounce more" onClick={() => { const n = rest + 1; if (n >= 16) set(lb + 1, n - 16); else set(lb, n); }}><Plus className="h-4 w-4" /></button>
    </div>
  );
}

function Chips<T extends string>({ options, value, onPick, label }: { options: { v: T; label: string }[]; value: T | null; onPick: (v: T) => void; label?: string }) {
  return (
    <div className="flex flex-wrap gap-1.5" role="group" aria-label={label}>
      {options.map((o) => (
        <button key={o.v} type="button" aria-pressed={value === o.v} onClick={() => { haptic("light"); onPick(o.v); }}
          className={cn("rounded-full border px-3 py-1.5 text-[13px] font-semibold [touch-action:manipulation]",
            value === o.v ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white text-slate-700 active:bg-slate-100")}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

const TYPE_OPTIONS = REFRIGERANTS.map((r) => ({ v: r as string, label: r as string }));
/** Old units are mostly R-22 / R-410A — those first. */
const OLD_TYPES = ["R-22", "R-410A", "R-454B", "R-32", "R-407C"];

/* ------------------------------------------------------------- the new unit */

/** Under a unit in "Going In": the refrigerant it holds and Log the Charge. */
export function RefrigerantUnitRow({ job, unit, log, dispatchId, empno, techFirst }: {
  job: Job; unit: JobEquipment; log: RefrigerantLog | undefined;
  dispatchId: string | null; empno: string | null; techFirst: string | null;
}) {
  const [open, setOpen] = useState(false);
  const type = log?.refrigerant ?? unit.refrigerant ?? null;
  const factory = log?.factory_oz ?? unit.factory_charge_oz ?? null;
  return (
    <>
      <div className={cn("mt-2 flex items-center gap-2.5 rounded-xl px-2.5 py-2", log ? "bg-white" : "bg-white ring-1 ring-sky-200")}>
        <Snowflake className={cn("h-4 w-4 shrink-0", log ? "text-emerald-600" : "text-sky-600")} />
        <span className="min-w-0 flex-1">
          {log ? (
            <>
              <span className="block text-[13px] font-bold text-slate-800">{type} · {ozText(log.oz)} added</span>
              <span className="block text-[11.5px] text-slate-500">
                {[log.total_oz != null ? `${ozText(log.total_oz)} total` : null, log.lineset_ft ? `${log.lineset_ft} ft line set` : null].filter(Boolean).join(" · ")}
              </span>
            </>
          ) : (
            <>
              <span className="block text-[13px] font-bold text-slate-800">{type ? `${type}${factory ? ` · factory ${ozText(factory)}` : ""}` : "Refrigerant"}</span>
              <span className="block text-[11.5px] text-slate-500">{type ? "Read off the data tag" : "Log what went in"}</span>
            </>
          )}
        </span>
        <button type="button" onClick={() => setOpen(true)}
          className={cn("m-btn shrink-0 !px-3 !py-2 !text-[13px]", log ? "m-btn-soft" : "m-btn-primary")}>
          {log ? "Edit" : "Log the Charge"}
        </button>
      </div>
      {open && <LogChargeSheet job={job} unit={unit} log={log} dispatchId={dispatchId} empno={empno} techFirst={techFirst} onClose={() => setOpen(false)} />}
    </>
  );
}

function LogChargeSheet({ job, unit, log, dispatchId, empno, techFirst, onClose }: {
  job: Job; unit: JobEquipment; log: RefrigerantLog | undefined;
  dispatchId: string | null; empno: string | null; techFirst: string | null; onClose: () => void;
}) {
  const save = useSaveCharge();
  const fromTag = !log && !!unit.refrigerant;
  const [type, setType] = useState<string | null>(log?.refrigerant ?? unit.refrigerant ?? null);
  const [factory, setFactory] = useState<number>(Number(log?.factory_oz ?? unit.factory_charge_oz ?? 0));
  const [factoryKnown, setFactoryKnown] = useState<boolean>((log?.factory_oz ?? unit.factory_charge_oz) != null);
  const [factoryFt, setFactoryFt] = useState<number>(Number(log?.factory_lineset_ft ?? unit.factory_lineset_ft ?? 15));
  const [lineset, setLineset] = useState<string>(log?.lineset_ft != null ? String(log.lineset_ft) : "");
  const [perFt, setPerFt] = useState<string>("0.6");
  const [added, setAdded] = useState<number>(Number(log?.oz ?? 0));
  const [metering, setMetering] = useState<"txv" | "piston" | null>(log?.metering ?? null);
  const [scA, setScA] = useState(log?.subcool_actual != null ? String(log.subcool_actual) : "");
  const [scT, setScT] = useState(log?.subcool_target != null ? String(log.subcool_target) : "");
  const [shA, setShA] = useState(log?.superheat_actual != null ? String(log.superheat_actual) : "");
  const [shT, setShT] = useState(log?.superheat_target != null ? String(log.superheat_target) : "");
  const [microns, setMicrons] = useState(log?.vacuum_microns != null ? String(log.vacuum_microns) : "");
  const [psi, setPsi] = useState(log?.leak_test_psi != null ? String(log.leak_test_psi) : "");
  const [brazed, setBrazed] = useState(log?.brazed_by ?? "");
  const [other, setOther] = useState(type && !REFRIGERANTS.includes(type as typeof REFRIGERANTS[number]) ? type : "");

  const linesetFt = numOrNull(lineset);
  const suggestion = suggestedAddOz(linesetFt, factoryFt, Number(perFt) || 0);
  const total = factoryKnown ? Math.round((factory + added) * 10) / 10 : null;
  const chosen = type === "Other" ? other.trim().toUpperCase() : type;
  const canSave = !!chosen && !save.isPending;

  const onSave = () => {
    if (!chosen) return;
    save.mutate({
      jobId: job.id, dispatchId, unit, refrigerant: chosen,
      factoryOz: factoryKnown ? factory : null, factoryLinesetFt: factoryKnown ? factoryFt : null,
      linesetFt, addedOz: added, metering,
      subcoolActual: metering === "txv" ? numOrNull(scA) : null, subcoolTarget: metering === "txv" ? numOrNull(scT) : null,
      superheatActual: metering === "piston" ? numOrNull(shA) : null, superheatTarget: metering === "piston" ? numOrNull(shT) : null,
      vacuumMicrons: numOrNull(microns), leakTestPsi: numOrNull(psi), brazedBy: brazed, note: null, empno,
    }, { onSuccess: () => { haptic("success"); onClose(); } });
  };

  const field = (id: string, label: string, value: string, set: (s: string) => void, placeholder?: string) => (
    <label htmlFor={id} className="block min-w-0">
      <span className="text-[12px] font-bold text-slate-600">{label}</span>
      <input id={id} inputMode="decimal" value={value} placeholder={placeholder}
        onChange={(e) => set(e.target.value.replace(/[^0-9.\-]/g, ""))} className={cn(input, "mt-1 tabular-nums")} />
    </label>
  );

  return (
    <Sheet title="Log the Charge" sub={[unit.category, [unit.manufacturer, unit.model].filter(Boolean).join(" "), unit.serial ? `S/N ${unit.serial}` : null].filter(Boolean).join(" · ")} onClose={onClose}
      footer={
        <button type="button" disabled={!canSave} onClick={onSave} className="m-btn m-btn-primary m-btn-big w-full">
          {save.isPending ? <Loader2 className="h-[18px] w-[18px] animate-spin" /> : <Check className="h-[18px] w-[18px]" />} Save the Charge
        </button>
      }>
      <section className={card}>
        <div className="flex items-center gap-2">
          <h4 className="text-[15px] font-bold tracking-tight text-slate-900">Refrigerant</h4>
          {fromTag && <span className="m-chip m-chip-good">Read from the tag</span>}
        </div>
        <div className="mt-2">
          <Chips label="Refrigerant" value={type && REFRIGERANTS.includes(type as typeof REFRIGERANTS[number]) ? type : type ? "Other" : null}
            options={[...TYPE_OPTIONS, { v: "Other", label: "Other" }]} onPick={(v) => setType(v)} />
        </div>
        {type === "Other" && (
          <input value={other} onChange={(e) => setOther(e.target.value)} placeholder="e.g. R-407C" autoCapitalize="characters" className={cn(input, "mt-2")} />
        )}
        <div className="mt-3 flex items-center justify-between gap-2">
          <span className="text-[13px] font-semibold text-slate-700">Factory charge</span>
          {factoryKnown ? (
            <LbOz oz={factory} onChange={setFactory} label="Factory charge" />
          ) : (
            <button type="button" onClick={() => setFactoryKnown(true)} className="m-btn m-btn-soft !px-3 !py-2 !text-[13px]">Add from the Plate</button>
          )}
        </div>
        {factoryKnown && (
          <div className="mt-2 flex items-center justify-between gap-2">
            <span className="text-[13px] font-semibold text-slate-700">Covers a line set of</span>
            <span className="flex items-center gap-1.5">
              <input inputMode="numeric" value={String(factoryFt || "")} onChange={(e) => setFactoryFt(Number(e.target.value.replace(/\D/g, "") || 0))}
                className="h-11 w-16 rounded-xl border border-slate-200 bg-white text-center text-[17px] font-bold tabular-nums focus:border-slate-400 focus:outline-none" aria-label="Feet the factory charge covers" />
              <span className="text-[12px] font-bold text-slate-500">ft</span>
            </span>
          </div>
        )}
      </section>

      <section className={card}>
        <h4 className="text-[15px] font-bold tracking-tight text-slate-900">What You Added</h4>
        <div className="mt-2 flex items-center justify-between gap-2">
          <label htmlFor="ls-ft" className="text-[13px] font-semibold text-slate-700">Line set length</label>
          <span className="flex items-center gap-1.5">
            <input id="ls-ft" inputMode="numeric" value={lineset} onChange={(e) => setLineset(e.target.value.replace(/\D/g, ""))} placeholder="—"
              className="h-11 w-16 rounded-xl border border-slate-200 bg-white text-center text-[17px] font-bold tabular-nums focus:border-slate-400 focus:outline-none" />
            <span className="text-[12px] font-bold text-slate-500">ft</span>
          </span>
        </div>
        {suggestion != null && factoryKnown && (
          <div className="mt-2 flex items-start gap-2.5 rounded-2xl bg-sky-50 px-3 py-2.5 text-sky-900">
            <Calculator className="mt-0.5 h-4 w-4 shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="text-[13.5px] font-bold">{suggestion === 0 ? "Nothing to add for the line set" : `About ${ozText(suggestion)} to add`}</div>
              <div className="mt-0.5 flex flex-wrap items-center gap-1 text-[12px]">
                {Math.max(0, (linesetFt ?? 0) - factoryFt)} ft past the factory {factoryFt} ft ×
                <input inputMode="decimal" value={perFt} onChange={(e) => setPerFt(e.target.value.replace(/[^0-9.]/g, ""))} aria-label="Ounces per extra foot"
                  className="h-7 w-12 rounded-lg border border-sky-200 bg-white text-center text-[12px] font-bold" /> oz per foot — check the unit&apos;s charging chart.
              </div>
              {suggestion > 0 && Math.abs(suggestion - added) > 0.05 && (
                <button type="button" onClick={() => setAdded(suggestion)} className="mt-1.5 text-[12.5px] font-bold text-sky-700 underline">Use {ozText(suggestion)}</button>
              )}
            </div>
          </div>
        )}
        <div className="mt-3 flex items-center justify-between gap-2">
          <span className="text-[13px] font-semibold text-slate-700">Weighed in</span>
          <LbOz oz={added} onChange={setAdded} label="Weighed in" />
        </div>
        {total != null && (
          <div className="mt-3 flex items-baseline justify-between gap-2 border-t border-slate-100 pt-2.5">
            <span className="text-[13.5px] font-bold text-slate-700">Total in the system</span>
            <span className="text-[22px] font-extrabold tabular-nums tracking-tight text-slate-900">{ozText(total)}</span>
          </div>
        )}
        {total != null && (
          <p className="mt-2 flex items-center gap-2 rounded-xl bg-slate-50 px-3 py-2 text-[12.5px] text-slate-600">
            <Tag className="h-3.5 w-3.5 shrink-0" /> Write {ozText(total)} on the unit&apos;s charging label too.
          </p>
        )}
      </section>

      <section className={card}>
        <div className="flex items-center gap-2">
          <h4 className="text-[15px] font-bold tracking-tight text-slate-900">Line Set &amp; Readings</h4>
          <InfoTip label="Where these came from">Lines 22–24 of the Install Order (who soldered the line set, leak test psi, microns vacuum) plus the subcool or superheat you charged to. The install checklist and the rebate test form read them from here.</InfoTip>
        </div>
        <label htmlFor="brazed" className="mt-2 block">
          <span className="text-[12px] font-bold text-slate-600">Who soldered the line set?</span>
          <input id="brazed" value={brazed} onChange={(e) => setBrazed(e.target.value)} placeholder="Name" className={cn(input, "mt-1")} />
        </label>
        {techFirst && !brazed.trim() && (
          <button type="button" onClick={() => setBrazed(techFirst)} className="mt-1.5 text-[12.5px] font-bold text-slate-600 underline">{techFirst}</button>
        )}
        <div className="mt-2 grid grid-cols-2 gap-2">
          {field("psi", "Leak test (psi)", psi, setPsi)}
          {field("microns", "Vacuum (microns)", microns, setMicrons)}
        </div>
        <div className="mt-3">
          <span className="text-[12px] font-bold text-slate-600">Metering device</span>
          <div className="mt-1.5">
            <Chips label="Metering device" value={metering} options={[{ v: "txv", label: "TXV" }, { v: "piston", label: "Piston (non-TXV)" }]} onPick={setMetering} />
          </div>
        </div>
        {metering === "txv" && (
          <div className="mt-2 grid grid-cols-2 gap-2">
            {field("sca", "Subcool °F", scA, setScA)}
            {field("sct", "Target subcool °F", scT, setScT)}
          </div>
        )}
        {metering === "piston" && (
          <div className="mt-2 grid grid-cols-2 gap-2">
            {field("sha", "Superheat °F", shA, setShA)}
            {field("sht", "Target superheat °F", shT, setShT)}
          </div>
        )}
      </section>
      {save.isError && <p className="text-center text-[12px] font-semibold text-rose-600">Didn&apos;t save — check the signal and try again.</p>}
    </Sheet>
  );
}

/* ------------------------------------------------------------- the old unit */

export interface RecoveryValue {
  refrigerant: string | null;
  oz: number;
  empty: boolean;
  tankId: string | null;
  nameplateOz: number | null;
}

/** Refrigerant Recovered — inside the Old Unit sheet, and on its own for a unit saved before. */
export function RecoveryFields({ value, onChange }: { value: RecoveryValue; onChange: (v: RecoveryValue) => void }) {
  const { data: tanksQ } = useRefrigerantTanks();
  const addTank = useAddTank();
  const [newLabel, setNewLabel] = useState<string | null>(null);
  const tanks = tanksQ ?? [];
  const set = (p: Partial<RecoveryValue>) => onChange({ ...value, ...p });
  const tank = tanks.find((t) => t.id === value.tankId) ?? null;
  const mixed = !!tank?.refrigerant && !!value.refrigerant && tank.refrigerant !== value.refrigerant && !value.empty;
  // Offer the tanks holding the same refrigerant (or not set yet) first.
  const sorted = useMemo(() => [...tanks].sort((a, b) => {
    const sa = !a.refrigerant || a.refrigerant === value.refrigerant ? 0 : 1;
    const sb = !b.refrigerant || b.refrigerant === value.refrigerant ? 0 : 1;
    return sa - sb || a.label.localeCompare(b.label, undefined, { numeric: true });
  }), [tanks, value.refrigerant]);
  const tankLine = (t: RefrigerantTank) => [t.label, t.refrigerant, t.contents_oz > 0 ? `${ozText(t.contents_oz)} in it` : "empty"].filter(Boolean).join(" · ");
  const otherMode = value.refrigerant === "" || (!!value.refrigerant && !OLD_TYPES.includes(value.refrigerant));

  return (
    <section className={cn(card, "ring-1 ring-sky-200")}>
      <div className="flex items-center gap-2">
        <Snowflake className="h-4 w-4 text-sky-600" />
        <h4 className="flex-1 text-[15px] font-bold tracking-tight text-slate-900">Refrigerant Recovered</h4>
        <InfoTip label="Why we log this">This is the EPA 608 record for the old unit: where it was, the date, the refrigerant, how much came out and which tank it went into. Pegasus keeps it and adds up the month for the office.</InfoTip>
      </div>
      <div className="mt-2">
        <Chips label="Refrigerant" value={otherMode ? "Other" : value.refrigerant}
          options={[...OLD_TYPES.map((r) => ({ v: r, label: r })), { v: "Other", label: "Other" }]}
          onPick={(v) => set({ refrigerant: v === "Other" ? "" : v })} />
      </div>
      {otherMode && (
        <input value={value.refrigerant ?? ""} onChange={(e) => set({ refrigerant: e.target.value.toUpperCase() })} placeholder="e.g. R-407C" className={cn(input, "mt-2")} />
      )}
      {value.nameplateOz != null && (
        <p className="mt-2 text-[12px] text-slate-500">The plate says it held {ozText(value.nameplateOz)}.</p>
      )}
      <div className="mt-3 flex items-center justify-between gap-2">
        <span className="text-[13px] font-semibold text-slate-700">Got out</span>
        {value.empty ? <span className="text-[13px] font-bold text-slate-500">Nothing — it was empty</span> : <LbOz oz={value.oz} onChange={(oz) => set({ oz })} label="Recovered" />}
      </div>
      <button type="button" aria-pressed={value.empty} onClick={() => set({ empty: !value.empty })}
        className={cn("mt-2 rounded-full border px-3 py-1.5 text-[12.5px] font-semibold", value.empty ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white text-slate-600")}>
        It was empty (leaked out)
      </button>
      {!value.empty && (
        <div className="mt-3">
          <span className="text-[12px] font-bold text-slate-600">Into recovery tank</span>
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {sorted.map((t) => (
              <button key={t.id} type="button" aria-pressed={value.tankId === t.id} onClick={() => { haptic("light"); set({ tankId: t.id }); }}
                className={cn("rounded-full border px-3 py-1.5 text-[12.5px] font-semibold",
                  value.tankId === t.id ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white text-slate-700 active:bg-slate-100")}>
                {tankLine(t)}
              </button>
            ))}
            {newLabel == null && (
              <button type="button" onClick={() => setNewLabel(`Tank ${tanks.length + 1}`)} className="rounded-full border border-dashed border-slate-300 px-3 py-1.5 text-[12.5px] font-semibold text-slate-600">
                + New Tank
              </button>
            )}
          </div>
          {newLabel != null && (
            <div className="mt-2 flex gap-2">
              <input value={newLabel} onChange={(e) => setNewLabel(e.target.value)} aria-label="New tank name" className={input} />
              <button type="button" disabled={!newLabel.trim() || addTank.isPending}
                onClick={() => addTank.mutate({ label: newLabel, refrigerant: value.refrigerant || null }, { onSuccess: (id) => { set({ tankId: id }); setNewLabel(null); } })}
                className="m-btn m-btn-primary shrink-0 !px-4">Add</button>
            </div>
          )}
          {mixed && (
            <p className="mt-2 flex items-start gap-1.5 rounded-xl bg-amber-50 px-3 py-2 text-[12px] font-semibold text-amber-800">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {tank!.label} holds {tank!.refrigerant}. Keep one refrigerant per tank — mixed gas is worth less at buyback.
            </p>
          )}
        </div>
      )}
    </section>
  );
}

export function recoveryReady(v: RecoveryValue): boolean {
  return !!(v.refrigerant && v.refrigerant.trim()) && (v.empty || (v.oz > 0 && !!v.tankId));
}

/** An old unit saved before it had its refrigerant logged. */
export function RecoverySheet({ job, unit, log, dispatchId, empno, onClose }: {
  job: Job; unit: RemovedUnit; log: RefrigerantLog | undefined; dispatchId: string | null; empno: string | null; onClose: () => void;
}) {
  const save = useSaveRecovery();
  const [v, setV] = useState<RecoveryValue>({
    refrigerant: log?.refrigerant ?? unit.refrigerant ?? null, oz: Number(log?.oz ?? 0), empty: !!log?.empty,
    tankId: log?.tank_id ?? null, nameplateOz: unit.nameplate_charge_oz ?? null,
  });
  return (
    <Sheet title="Refrigerant Recovered" sub={[unit.category, [unit.brand, unit.model].filter(Boolean).join(" ")].filter(Boolean).join(" · ")} onClose={onClose}
      footer={
        <button type="button" disabled={!recoveryReady(v) || save.isPending}
          onClick={() => save.mutate({ jobId: job.id, dispatchId, removedId: unit.id, refrigerant: v.refrigerant!.trim(), oz: v.oz, empty: v.empty, tankId: v.tankId, nameplateOz: v.nameplateOz, note: null, empno },
            { onSuccess: () => { haptic("success"); onClose(); } })}
          className="m-btn m-btn-primary m-btn-big w-full">
          {save.isPending ? <Loader2 className="h-[18px] w-[18px] animate-spin" /> : <Check className="h-[18px] w-[18px]" />} Save
        </button>
      }>
      <RecoveryFields value={v} onChange={setV} />
      {save.isError && <p className="text-center text-[12px] font-semibold text-rose-600">Didn&apos;t save — check the signal and try again.</p>}
    </Sheet>
  );
}
